import React, { useEffect, useState } from "react"
import PageHeading from "components/common/LayoutComponents/PageHeading"
import Button from "@mui/material/Button"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableContainer from "@mui/material/TableContainer"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import Paper from "@mui/material/Paper"
import LoadingScreen from "components/common/LoadingScreen"
import { PERMISSIONS } from "constants/permission"
import { UsePermissionVerification } from "components/customHooks"
import { searchModel } from "services/modal"
import { verifyResponse } from "utils/httpRequest"
import ContentCopyIcon from "@mui/icons-material/ContentCopy"
import OpenInNewIcon from "@mui/icons-material/OpenInNew"
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded"
import { useSnackbar } from "notistack"
import { buildDashboardModalUrl } from "utils/platformUrl"
import ModelDlg from "./ModalDlg"
import { StyledModal } from "./Modal.style"

const Modal = () => {
  const [modals, setModals] = useState([])
  const [loading, setLoading] = useState(false)
  const { enqueueSnackbar } = useSnackbar()

  const AccessControl = UsePermissionVerification()

  const [openModalDialog, modalDialog, modalDialogLoading] = ModelDlg({
    onSubmit: () => {
      refreshModals()
    },
    access: AccessControl.Access,
  })

  const refreshModals = async () => {
    setLoading(true)
    try {
      const response = await searchModel()
      verifyResponse(response)
      setModals(response?.data?.list || [])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    refreshModals()
  }, [])

  useEffect(() => {
    AccessControl.SetAccess({
      ...AccessControl.Access,
      listModalAccess: AccessControl.VerifyAccess([
        PERMISSIONS.MODAL.MODAL_LIST,
      ]),
      addModalAccess: AccessControl.VerifyAccess([PERMISSIONS.MODAL.MODAL_ADD]),
      editModalAccess: AccessControl.VerifyAccess([
        PERMISSIONS.MODAL.MODAL_EDIT,
      ]),
      deleteModalAccess: AccessControl.VerifyAccess([
        PERMISSIONS.MODAL.MODAL_DELETE,
      ]),
      addModalContentAccess: AccessControl.VerifyAccess([
        PERMISSIONS.MODAL.MODAL_CONTENT_ADD,
      ]),
      editModalContentAccess: AccessControl.VerifyAccess([
        PERMISSIONS.MODAL.MODAL_CONTENT_EDIT,
      ]),
      deleteModalContentAccess: AccessControl.VerifyAccess([
        PERMISSIONS.MODAL.MODAL_CONTENT_DELETE,
      ]),
      exchangeIndexModalContentAccess: AccessControl.VerifyAccess([
        PERMISSIONS.MODAL.MODAL_CONTENT_INDEX_EXCHANGE,
      ]),
      listTypeModalContentAccess: AccessControl.VerifyAccess([
        PERMISSIONS.MODAL.MODAL_LIST_ALL_CONTENT_TYPES,
      ]),
    })
  }, [AccessControl.UserPermissions])

  return (
    <StyledModal>
      <PageHeading>
        <div className="heading-copy">
          <h1 className="page-title">Modal Management</h1>
          <p className="page-subtitle">
            Manage marketing and announcement modals. The onboarding modal series now lives in User Management.
          </p>
        </div>
        <div className="actions">
          {AccessControl.Access.addModalAccess && (
            <Button
              className="new-modal-btn"
              variant="contained"
              size="medium"
              startIcon={<AddCircleOutlineRoundedIcon />}
              onClick={() => {
                openModalDialog()
              }}
            >
              New Modal Item
            </Button>
          )}
        </div>
      </PageHeading>
      <div className="page-surface">
        <>
          {!AccessControl.Access.listModalAccess && (
            <div className="empty-row">
              You do not have permission to view modal records.
            </div>
          )}
          {AccessControl.Access.listModalAccess && (
            <TableContainer component={Paper}>
              <Table sx={{ minWidth: 650 }} aria-label="simple table">
                <TableHead>
                  <TableRow>
                    <TableCell>Tag</TableCell>
                    <TableCell align="left">Description</TableCell>
                    <TableCell align="left">Dashboard Link</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {modals.length === 0 && (
                    <TableRow>
                      <TableCell className="empty-row" colSpan={4}>
                        No modal definitions are available.
                      </TableCell>
                    </TableRow>
                  )}
                  {modals.map((row) => (
                    <TableRow
                      key={`modal-row-${row.id}`}
                      sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                    >
                      <TableCell component="th" scope="row">
                        {row.tag}
                      </TableCell>
                      <TableCell align="left">{row.description}</TableCell>
                      <TableCell align="left">
                        <div className="modal-link-cell">
                          <a
                            href={buildDashboardModalUrl(row.tag)}
                            target="_blank"
                            rel="noreferrer"
                          >
                            Open Link
                            <OpenInNewIcon fontSize="inherit" />
                          </a>
                          <Button
                            className="action-btn"
                            variant="text"
                            size="small"
                            startIcon={<ContentCopyIcon />}
                            onClick={async () => {
                              const link = buildDashboardModalUrl(row.tag)
                              try {
                                await navigator.clipboard.writeText(link)
                                enqueueSnackbar("Dashboard modal link copied", {
                                  variant: "success",
                                })
                              } catch (error) {
                                enqueueSnackbar(
                                  "Unable to copy. Please copy the link manually.",
                                  {
                                    variant: "warning",
                                  }
                                )
                              }
                            }}
                          >
                            Copy
                          </Button>
                        </div>
                      </TableCell>
                      <TableCell align="right">
                        <div className="table-actions">
                          {AccessControl.Access.editModalAccess && (
                            <Button
                              className="action-btn"
                              variant="contained"
                              size="medium"
                              onClick={() => {
                                openModalDialog(row)
                              }}
                            >
                              Edit
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      </div>
      <LoadingScreen
        loading={loading || modalDialogLoading}
      />
      {modalDialog}
    </StyledModal>
  )
}

export default Modal
